import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';

export const SHARE_LOCATION_LOW_ACCURACY_METERS = 100;
export const SHARE_LOCATION_TIMEOUT_MS = 10_000;

export const SHARE_LOCATION_COPY = {
  shareText: 'Share where this is.',
  button: 'Share my location',
  received: 'Location received. You can close this page.',
  movedOn: "We couldn't save that location. You can close this page.",
  unavailable: "We couldn't get your location. Reply to the text with an address instead.",
  used: 'This link has already been used or has expired. Reply to the text with an address instead.',
};

export function shareLocationHeading(agencyName?: string | null): string {
  const agency = (agencyName || '').trim();

  return agency ? `${agency} Text a Photo` : 'Text a Photo';
}

export function shareLocationOutsideMessage(agencyName?: string | null): string {
  const agency = (agencyName || '').trim() || 'the agency';

  return `That spot is outside ${agency}. We've texted you a new link.`;
}

type ShareView = 'loading' | 'open' | 'received' | 'movedOn' | 'outside' | 'unavailable' | 'used';

interface ShareLocationRouteParams {
  token: string;
}

interface ShareLocationResponse {
  status?: 'OPEN' | 'USED' | 'EXPIRED';
  agencyName?: string;
}

interface ShareLocationResult {
  result?: 'ACCEPTED' | 'MOVED_ON' | 'OUTSIDE_AGENCY';
  reasked?: boolean;
}

function apiUrl(path: string): string {
  const baseUrl = process.env.API_URL;
  if (!baseUrl) {
    throw new Error('API_URL is not configured');
  }

  return `${baseUrl.replace(/\/$/, '')}/${path}`;
}

function responseStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

const pageStyle: React.CSSProperties = {
  maxWidth: 420,
  margin: '48px auto',
  padding: 24,
  color: '#1a1a1a',
  fontFamily: 'sans-serif',
};

const buttonStyle: React.CSSProperties = {
  fontSize: 18,
  padding: '12px 16px',
};

export default function StaffTextShareLocation({ match }: RouteComponentProps<ShareLocationRouteParams>) {
  const token = match.params.token;
  const [view, setView] = useState<ShareView>('loading');
  const [agencyName, setAgencyName] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    axios
      .get<ShareLocationResponse>(apiUrl(`public/staff-text/location/${encodeURIComponent(token)}`), {
        signal: controller.signal,
      })
      .then((response) => {
        setAgencyName(response.data?.agencyName);
        setView(response.data?.status === 'OPEN' ? 'open' : 'used');
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        setView(responseStatus(error) === 410 ? 'used' : 'unavailable');
      });

    return () => controller.abort();
  }, [token]);

  const share = () => {
    if (submitting) {
      return;
    }
    if (!navigator.geolocation) {
      setView('unavailable');

      return;
    }
    setSubmitting(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        if (!Number.isFinite(accuracy) || accuracy > SHARE_LOCATION_LOW_ACCURACY_METERS) {
          setSubmitting(false);
          setView('unavailable');

          return;
        }
        axios
          .post<ShareLocationResult>(apiUrl(`public/staff-text/location/${encodeURIComponent(token)}`), {
            lat: latitude,
            long: longitude,
            accuracyM: accuracy,
          })
          .then((response) => {
            if (response.data?.result === 'ACCEPTED') {
              setView('received');
            } else if (response.data?.result === 'MOVED_ON') {
              setView('movedOn');
            } else if (response.data?.result === 'OUTSIDE_AGENCY' && response.data.reasked) {
              setView('outside');
            } else {
              setView('unavailable');
            }
          })
          .catch((error) => {
            setView(responseStatus(error) === 410 ? 'used' : 'unavailable');
          })
          .finally(() => setSubmitting(false));
      },
      () => {
        setSubmitting(false);
        setView('unavailable');
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: SHARE_LOCATION_TIMEOUT_MS }
    );
  };

  const message =
    view === 'received'
      ? SHARE_LOCATION_COPY.received
      : view === 'movedOn'
      ? SHARE_LOCATION_COPY.movedOn
      : view === 'outside'
      ? shareLocationOutsideMessage(agencyName)
      : view === 'unavailable'
      ? SHARE_LOCATION_COPY.unavailable
      : view === 'used'
      ? SHARE_LOCATION_COPY.used
      : '';

  return (
    <main style={pageStyle}>
      <h1>{shareLocationHeading(agencyName)}</h1>
      {view === 'loading' && <p role="status">Loading</p>}
      {view === 'open' && (
        <>
          <p>{SHARE_LOCATION_COPY.shareText}</p>
          <button type="button" onClick={share} disabled={submitting} style={buttonStyle}>
            {SHARE_LOCATION_COPY.button}
          </button>
        </>
      )}
      <div role="status" aria-live="polite">
        {message}
      </div>
    </main>
  );
}
